import { type ReactElement } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from '@/lib/auth'
import { AppLayout } from '@/components/layouts/app-layout'
import { RequireAuth, RedirectIfAuth } from '@/components/layouts/guards'
import { LandingPage } from '@/pages/landing'
import { LoginPage, SignupPage } from '@/pages/auth'
import { PaymentReturn } from '@/pages/payment-return'
import {
  ClientDashboard,
  ClientCatalog,
  ClientNewRequest,
  ClientRequests,
  ClientRequestDetail,
  ClientOrders,
  ClientOrderDetail,
  ClientProfile,
} from '@/pages/client'
import {
  AdminDashboard,
  AdminRequests,
  AdminRequestDetail,
  AdminOrders,
  AdminClients,
  AdminServices,
  AdminProducts,
  AdminReviews,
} from '@/pages/admin'

function ClientShell({ children }: { children: ReactElement }): ReactElement {
  return (
    <RequireAuth role="CLIENT">
      <AppLayout>{children}</AppLayout>
    </RequireAuth>
  )
}

function AdminShell({ children }: { children: ReactElement }): ReactElement {
  return (
    <RequireAuth role="ADMIN">
      <AppLayout>{children}</AppLayout>
    </RequireAuth>
  )
}

export function App(): ReactElement {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route
            path="/login"
            element={
              <RedirectIfAuth>
                <LoginPage />
              </RedirectIfAuth>
            }
          />
          <Route
            path="/signup"
            element={
              <RedirectIfAuth>
                <SignupPage />
              </RedirectIfAuth>
            }
          />

          <Route
            path="/client"
            element={
              <ClientShell>
                <ClientDashboard />
              </ClientShell>
            }
          />
          <Route
            path="/client/catalog"
            element={
              <ClientShell>
                <ClientCatalog />
              </ClientShell>
            }
          />
          <Route
            path="/client/requests/new"
            element={
              <ClientShell>
                <ClientNewRequest />
              </ClientShell>
            }
          />
          <Route
            path="/client/requests"
            element={
              <ClientShell>
                <ClientRequests />
              </ClientShell>
            }
          />
          <Route
            path="/client/requests/:id"
            element={
              <ClientShell>
                <ClientRequestDetail />
              </ClientShell>
            }
          />
          <Route
            path="/client/orders"
            element={
              <ClientShell>
                <ClientOrders />
              </ClientShell>
            }
          />
          <Route
            path="/client/orders/:id"
            element={
              <ClientShell>
                <ClientOrderDetail />
              </ClientShell>
            }
          />
          <Route
            path="/client/profile"
            element={
              <ClientShell>
                <ClientProfile />
              </ClientShell>
            }
          />
          <Route path="/client/payment/return" element={<PaymentReturn />} />

          <Route
            path="/admin"
            element={
              <AdminShell>
                <AdminDashboard />
              </AdminShell>
            }
          />
          <Route
            path="/admin/requests"
            element={
              <AdminShell>
                <AdminRequests />
              </AdminShell>
            }
          />
          <Route
            path="/admin/requests/:id"
            element={
              <AdminShell>
                <AdminRequestDetail />
              </AdminShell>
            }
          />
          <Route path="/admin/quotes" element={<Navigate to="/admin/requests" replace />} />
          <Route
            path="/admin/orders"
            element={
              <AdminShell>
                <AdminOrders />
              </AdminShell>
            }
          />
          <Route
            path="/admin/clients"
            element={
              <AdminShell>
                <AdminClients />
              </AdminShell>
            }
          />
          <Route
            path="/admin/services"
            element={
              <AdminShell>
                <AdminServices />
              </AdminShell>
            }
          />
          <Route
            path="/admin/products"
            element={
              <AdminShell>
                <AdminProducts />
              </AdminShell>
            }
          />
          <Route
            path="/admin/reviews"
            element={
              <AdminShell>
                <AdminReviews />
              </AdminShell>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  )
}
